// src/api/authApi.ts
import axios from "axios";
import apiClient from "./apiClient";

const BASE_URL = "https://gg-api.nationtonation.ca/api/auth";

// Store tokens and last activity time
let refreshTimeout: NodeJS.Timeout;
let inactivityTimeout: NodeJS.Timeout;

// Token refresh scheduler
const startTokenRefresh = () => {
  // Clear any existing timeout
  if (refreshTimeout) clearTimeout(refreshTimeout);
  
  // Set new timeout to refresh token before it expires (14 minutes for a 15-minute token)
  refreshTimeout = setTimeout(async () => {
    try {
      await refreshAccessToken();
      startTokenRefresh(); // Restart the refresh cycle
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
    }
  }, 14 * 60 * 1000); // 14 minutes
};

// Inactivity timer
const resetInactivityTimer = () => {
  // Clear existing timeout
  if (inactivityTimeout) clearTimeout(inactivityTimeout);
  
  // Set new timeout (1 hour of inactivity)
  inactivityTimeout = setTimeout(() => {
    logout();
  }, 60 * 60 * 1000); // 1 hour
};

// Initialize activity listeners
const setupActivityListeners = () => {
  const activityEvents = ["mousemove", "keydown", "scroll", "click"];
  
  activityEvents.forEach(event => {
    window.addEventListener(event, resetInactivityTimer);
  });
};

// Login function
const login = async (email: string, password: string) => {
  const response = await axios.post(
    `${BASE_URL}/login`,
    { email, password },
    { withCredentials: true } // 🔐 Add this
  );

  localStorage.setItem("token", response.data.token);

  startTokenRefresh();
  resetInactivityTimer();

  return response.data;
};


// Fetch admin details
const fetchAdminDetails = async () => {
  try {
    const response = await apiClient.get(`${BASE_URL}/me`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    resetInactivityTimer();
    return response.data;
  } catch (error: any) {
    // If token expired, try to refresh once
    if (error.response?.status === 403) {
      try {
        const newToken = await refreshAccessToken();
        const retryResponse = await apiClient.get(`${BASE_URL}/me`, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        resetInactivityTimer();
        return retryResponse.data;
      } catch (refreshError) {
        console.error("Token refresh failed during fetchAdminDetails.");
        throw refreshError;
      }
    }
    throw error;
  }
};


// Refresh token function
const refreshAccessToken = async () => {
  try {
    const response = await axios.post(
      `${BASE_URL}/refresh`,
      {},
      { withCredentials: true } // 👈 send cookie
    );

    // Store new access token only
    localStorage.setItem("token", response.data.token);
    return response.data.token;
  } catch (error) {
    console.error("Error refreshing access token:", error);
    logout();
    throw error;
  }
};


// Logout function
const logout = () => {
  // Clear tokens
  localStorage.removeItem("token");
  // localStorage.removeItem("refreshToken");
  
  // Clear timers
  if (refreshTimeout) clearTimeout(refreshTimeout);
  if (inactivityTimeout) clearTimeout(inactivityTimeout);
  
  // Redirect to login or perform other cleanup
  window.location.href = "/login";
};

// Export all functions consistently
export {
  login,
  logout,
  fetchAdminDetails,
  refreshAccessToken,
  startTokenRefresh,
  setupActivityListeners,
  resetInactivityTimer
};